
import React, { useState, useEffect } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ImageUploaderProps } from './types';
import ImagePreview from './ImagePreview';
import UploadDropzone from './UploadDropzone';
import { validateFile } from './fileValidation';
import { uploadFileToSupabase } from './uploadService';

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUpload,
  onError,
  accept,
  bucketName,
  existingUrl,
  currentUrl,
  currentImageUrl,
  type,
  maxSizeInMB = 5, // Default max size: 5MB
  className,
  previewClassName,
  buttonLabel,
  placeholderText,
  showPreview = true,
  previewHeight = 'max-h-48',
  disabled = false
}) => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  // Use the first non-undefined URL from the various prop options
  const initialUrl = existingUrl || currentUrl || currentImageUrl || null;
  const [preview, setPreview] = useState<string | null>(initialUrl);
  const [error, setError] = useState<string | null>(null);

  // Update preview when props change
  useEffect(() => {
    const newUrl = existingUrl || currentUrl || currentImageUrl || null;
    if (newUrl !== preview) {
      setPreview(newUrl);
    }
  }, [existingUrl, currentUrl, currentImageUrl, preview]);

  const handleFileSelect = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      // Validate file before uploading
      const validationResult = validateFile(file, maxSizeInMB, accept);
      
      if (!validationResult.valid) {
        setError(validationResult.error);
        toast({
          title: t('admin.error', 'Error'),
          description: validationResult.error,
          variant: 'destructive',
        });
        if (onError) onError(new Error(validationResult.error || 'File validation failed'));
        return;
      }

      // Upload file to Supabase
      const publicUrl = await uploadFileToSupabase(file, bucketName);
      
      // Update state and call callback
      setPreview(publicUrl);
      onUpload(publicUrl);
      
      toast({
        title: t('admin.success', 'Success'),
        description: t('admin.imageUploaded', 'Image uploaded successfully'),
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error uploading image';
      setError(errorMessage);
      toast({
        title: t('admin.error', 'Error'),
        description: t('admin.errorUploadingImage', 'Error uploading image'),
        variant: 'destructive',
      });
      if (onError && error instanceof Error) {
        onError(error);
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setPreview(null);
    setError(null);
    onUpload('');
  };

  return (
    <div className={cn("space-y-2", className)}>
      {showPreview && preview ? (
        <ImagePreview 
          imageUrl={preview}
          onRemove={removeImage}
          previewHeight={previewHeight}
          previewClassName={previewClassName}
          disabled={disabled}
        />
      ) : (
        <UploadDropzone
          onFileSelect={handleFileSelect}
          accept={accept}
          uploading={uploading}
          error={error}
          placeholderText={placeholderText}
          buttonLabel={buttonLabel}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default ImageUploader;
